import type { DateTime } from "luxon";
import { type FacetDimension, valueOrNotSet } from "../../../utils/facetFilters";
import type { PbjStaffingHoursRow } from "../../../utils/models";
import type { WithBuilding } from "../network/buildingQueries";
import { dayTypeFacet } from "../pbjFacets";
import { toAuditDate } from "../pbjGridFormat";

export enum StaffingFacet {
	Review = "review",
	DayType = "day_type",
	PbjJob = "pbj_job",
	JobDescription = "job_description",
	Department = "department",
	PayType = "pay_type",
	EmploymentType = "employment_type",
	WorkerType = "worker_type",
	Agency = "agency",
	EarnCode = "earn_code",
}

export enum StaffingReviewFlag {
	NoPbjJob = "no_pbj_job",
	NoPayType = "no_pay_type",
	Capped = "capped",
	Dst = "dst",
	PayTypeSetByHand = "pay_type_set_by_hand",
}

const staffingDateCache = new WeakMap<PbjStaffingHoursRow, DateTime>();

/** The row's dept-local date, cached — the filters read it for every row on every change. */
export const staffingDateOf = (row: PbjStaffingHoursRow): DateTime => {
	const cached = staffingDateCache.get(row);
	if (cached) return cached;
	const date = toAuditDate(row.date);
	if (!date) throw new Error(`Staffing Hours row for ${row.empId} has an unreadable date: ${String(row.date)}`);
	staffingDateCache.set(row, date);
	return date;
};

export const reviewFlagsOf = (row: PbjStaffingHoursRow): StaffingReviewFlag[] => {
	const flags: StaffingReviewFlag[] = [];
	if (row.pbjJobId == null) flags.push(StaffingReviewFlag.NoPbjJob);
	if (row.payTypeCode == null) flags.push(StaffingReviewFlag.NoPayType);
	if (row.cappedHours != null) flags.push(StaffingReviewFlag.Capped);
	if (row.dstAffected) flags.push(StaffingReviewFlag.Dst);
	if (row.payTypeCodeOverride != null) flags.push(StaffingReviewFlag.PayTypeSetByHand);
	return flags;
};

export const STAFFING_DIMENSIONS: Record<StaffingFacet, FacetDimension<PbjStaffingHoursRow>> = {
	[StaffingFacet.Review]: { id: StaffingFacet.Review, getKeys: reviewFlagsOf },
	[StaffingFacet.DayType]: dayTypeFacet(StaffingFacet.DayType, staffingDateOf),
	[StaffingFacet.PbjJob]: { id: StaffingFacet.PbjJob, getKeys: (r) => valueOrNotSet(r.pbjJobId) },
	[StaffingFacet.JobDescription]: { id: StaffingFacet.JobDescription, getKeys: (r) => valueOrNotSet(r.jobDescription) },
	[StaffingFacet.Department]: { id: StaffingFacet.Department, getKeys: (r) => valueOrNotSet(r.departmentName) },
	[StaffingFacet.PayType]: { id: StaffingFacet.PayType, getKeys: (r) => valueOrNotSet(r.payTypeCode) },
	[StaffingFacet.EmploymentType]: { id: StaffingFacet.EmploymentType, getKeys: (r) => valueOrNotSet(r.employmentType) },
	[StaffingFacet.WorkerType]: { id: StaffingFacet.WorkerType, getKeys: (r) => valueOrNotSet(r.w2OrContractor) },
	[StaffingFacet.Agency]: { id: StaffingFacet.Agency, getKeys: (r) => valueOrNotSet(r.agencyName) },
	[StaffingFacet.EarnCode]: { id: StaffingFacet.EarnCode, getKeys: (r) => valueOrNotSet(r.earnCode) },
};

export const STAFFING_DIMENSION_LIST: FacetDimension<PbjStaffingHoursRow>[] = Object.values(STAFFING_DIMENSIONS);

/** The facet a column's "Filter by…" opens. */
/** A Staffing Hours grid row: the API row plus the building it came from. */
export type StaffingGridRow = WithBuilding<PbjStaffingHoursRow>;

export const COLUMN_FACET: Readonly<Record<string, string>> = {
	jobDescription: StaffingFacet.JobDescription,
	payTitleCode: StaffingFacet.PbjJob,
	pbjJobId: StaffingFacet.PbjJob,
	payTypeCode: StaffingFacet.PayType,
	date: StaffingFacet.DayType,
	departmentName: StaffingFacet.Department,
	earnCode: StaffingFacet.EarnCode,
	agencyName: StaffingFacet.Agency,
	w2OrContractor: StaffingFacet.WorkerType,
	employmentType: StaffingFacet.EmploymentType,
};
